import { FormEvent, useEffect, useState } from 'react';
import { api, getToken } from '../../api/client';
import { t } from '../../hooks/useT';
import { AdminCropEditor } from './AdminCropEditor';
import { AdminPhotoPicker } from './AdminPhotoPicker';
import { AdminPuzzlePreview } from './AdminPuzzlePreview';
import type { PhotoAttributionData } from '../PhotoAttribution';

interface CropMeta {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface ZoomLevel {
  level: number;
  imageUrl: string;
  cropMeta?: CropMeta;
}

interface AdminPuzzle {
  id: string;
  slug: string;
  puzzleNumber: number;
  publishDate: string | null;
  status: string;
  pool: string;
  answerCanonical: string;
  answerEn?: string | null;
  searchQuery?: string | null;
  photoProvider?: string | null;
  imageVersion?: number;
  originalImageUrl?: string | null;
  adminReviewed: boolean;
  aliases: string[];
  zoomLevels: ZoomLevel[];
  attribution?: PhotoAttributionData | null;
}

type PhotoProviderChoice = 'auto' | 'pexels' | 'unsplash';

function PuzzleCard({
  puzzle,
  onSaved,
  onEditCrops,
  onPreview,
  onPickPhoto,
}: {
  puzzle: AdminPuzzle;
  onSaved: (p: AdminPuzzle) => void;
  onEditCrops: (puzzle: AdminPuzzle) => void;
  onPreview: (puzzle: AdminPuzzle) => void;
  onPickPhoto: (puzzle: AdminPuzzle, params: {
    answer: string;
    searchQuery: string;
    provider: PhotoProviderChoice;
  }) => void;
}) {
  const [answer, setAnswer] = useState(puzzle.answerCanonical);
  const [answerEn, setAnswerEn] = useState(puzzle.answerEn || '');
  const [searchQuery, setSearchQuery] = useState(puzzle.searchQuery || '');
  const [provider, setProvider] = useState<PhotoProviderChoice>('auto');
  const [aliases, setAliases] = useState(puzzle.aliases.join(', '));
  const [level, setLevel] = useState(0);
  const [busy, setBusy] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setAnswer(puzzle.answerCanonical);
    setAnswerEn(puzzle.answerEn || '');
    setSearchQuery(puzzle.searchQuery || '');
    setAliases(puzzle.aliases.join(', '));
    setLevel(0);
  }, [
    puzzle.id,
    puzzle.answerCanonical,
    puzzle.answerEn,
    puzzle.searchQuery,
    puzzle.aliases,
    puzzle.adminReviewed,
    puzzle.imageVersion,
  ]);

  const imageUrl = puzzle.zoomLevels.find((z) => z.level === level)?.imageUrl
    || puzzle.zoomLevels[0]?.imageUrl;

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      const res = await api<{ puzzle: AdminPuzzle }>(`/admin/puzzles/${puzzle.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ answer, aliases, searchQuery, answerEn }),
      });
      onSaved(res.puzzle);
      setMsg(t('admin.puzzles.saved'));
    } catch {
      setMsg(t('admin.puzzles.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const handleToggleReviewed = async () => {
    setReviewBusy(true);
    setMsg('');
    try {
      const res = await api<{ puzzle: AdminPuzzle }>(`/admin/puzzles/${puzzle.id}/reviewed`, {
        method: 'PATCH',
        body: JSON.stringify({ reviewed: !puzzle.adminReviewed }),
      });
      onSaved(res.puzzle);
    } catch {
      setMsg(t('admin.puzzles.reviewToggleError'));
    } finally {
      setReviewBusy(false);
    }
  };

  const handleRefetchPhoto = () => {
    if (!answer.trim()) return;
    onPickPhoto(puzzle, {
      answer: answer.trim(),
      searchQuery: searchQuery.trim(),
      provider,
    });
  };

  const handleRegenerateHd = async () => {
    setRegenBusy(true);
    setMsg('');
    try {
      const res = await api<{ puzzle: AdminPuzzle }>(`/admin/puzzles/${puzzle.id}/regenerate-crops`, {
        method: 'POST',
      });
      onSaved(res.puzzle);
      setLevel(0);
      setMsg(t('admin.puzzles.regenerateHdDone'));
    } catch {
      setMsg(t('admin.puzzles.regenerateHdError'));
    } finally {
      setRegenBusy(false);
    }
  };

  return (
    <article className={`admin-puzzle-card admin-puzzle-card--compact${puzzle.adminReviewed ? ' admin-puzzle-card--reviewed' : ''}`}>
      <header className="admin-puzzle-head">
        <span className="admin-puzzle-num">#{puzzle.puzzleNumber}</span>
        <span className="admin-badge">{puzzle.pool}</span>
        <span className="admin-badge admin-badge-muted">{puzzle.status}</span>
        {puzzle.photoProvider && (
          <span className="admin-badge admin-badge-provider">{puzzle.photoProvider}</span>
        )}
      </header>

      <div className="admin-puzzle-thumb-block">
        {imageUrl && (
          <div className="admin-puzzle-thumb">
            <img src={imageUrl} alt="" />
            <button
              type="button"
              className={`admin-puzzle-valid-btn${puzzle.adminReviewed ? ' is-valid' : ''}`}
              onClick={() => void handleToggleReviewed()}
              disabled={reviewBusy}
              title={puzzle.adminReviewed ? t('admin.puzzles.markUnvalidated') : t('admin.puzzles.markValidated')}
              aria-label={puzzle.adminReviewed ? t('admin.puzzles.markUnvalidated') : t('admin.puzzles.markValidated')}
              aria-pressed={puzzle.adminReviewed}
            >
              ✓
            </button>
          </div>
        )}
        {puzzle.zoomLevels.length > 0 && (
          <div className="admin-zoom-strip">
            {puzzle.zoomLevels.map((z) => (
              <button
                key={z.level}
                type="button"
                className={`admin-zoom-btn admin-zoom-btn--compact${level === z.level ? ' active' : ''}`}
                onClick={() => setLevel(z.level)}
              >
                {z.level}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="admin-puzzle-fields">
        <label className="admin-puzzle-field admin-puzzle-field--compact">
          <span>{t('admin.puzzles.searchQuery')}</span>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('admin.puzzles.searchQueryPlaceholder')}
          />
        </label>
        <label className="admin-puzzle-field admin-puzzle-field--compact">
          <span>{t('admin.puzzles.photoProvider')}</span>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value as PhotoProviderChoice)}
            aria-label={t('admin.puzzles.photoProvider')}
          >
            <option value="auto">{t('admin.puzzles.providerAuto')}</option>
            <option value="pexels">{t('admin.puzzles.providerPexels')}</option>
            <option value="unsplash">{t('admin.puzzles.providerUnsplash')}</option>
          </select>
        </label>
        <div className="admin-puzzle-actions-stack">
          <button
            type="button"
            className="btn secondary admin-btn-sm"
            onClick={() => onPreview(puzzle)}
            disabled={puzzle.zoomLevels.length === 0}
          >
            {t('admin.puzzles.preview')}
          </button>
          <button
            type="button"
            className="btn secondary admin-btn-sm"
            disabled={!answer.trim()}
            onClick={handleRefetchPhoto}
          >
            {t('admin.puzzles.replaceImageShort')}
          </button>
          <button
            type="button"
            className="btn secondary admin-btn-sm"
            onClick={() => onEditCrops(puzzle)}
          >
            {t('admin.puzzles.editCrops')}
          </button>
          {puzzle.originalImageUrl && (
            <button
              type="button"
              className="btn secondary admin-btn-sm"
              disabled={regenBusy}
              onClick={() => void handleRegenerateHd()}
            >
              {regenBusy ? t('common.loading') : t('admin.puzzles.regenerateHd')}
            </button>
          )}
        </div>

        <form className="admin-form admin-form-compact" onSubmit={handleSave}>
          <label className="admin-puzzle-field admin-puzzle-field--compact">
            <span>{t('admin.puzzles.answer')}</span>
            <input value={answer} onChange={(e) => setAnswer(e.target.value)} required />
          </label>
          <label className="admin-puzzle-field admin-puzzle-field--compact">
            <span>{t('admin.puzzles.answerEn')}</span>
            <input value={answerEn} onChange={(e) => setAnswerEn(e.target.value)} />
          </label>
          <label className="admin-puzzle-field admin-puzzle-field--compact">
            <span>{t('admin.puzzles.aliases')}</span>
            <input value={aliases} onChange={(e) => setAliases(e.target.value)} />
          </label>
          <button type="submit" className="btn primary admin-btn-sm" disabled={busy}>
            {t('admin.puzzles.save')}
          </button>
        </form>
      </div>

      {msg && <p className="admin-msg admin-msg--compact">{msg}</p>}
    </article>
  );
}

export function AdminPuzzlesTab() {
  const [puzzles, setPuzzles] = useState<AdminPuzzle[]>([]);
  const [pool, setPool] = useState('daily');
  const [status, setStatus] = useState('');
  const [reviewed, setReviewed] = useState('');
  const [sort, setSort] = useState('number_asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createMsg, setCreateMsg] = useState('');
  const [batchMsg, setBatchMsg] = useState('');
  const [batchBusy, setBatchBusy] = useState<'daily' | 'premium' | null>(null);
  const [cropPuzzleId, setCropPuzzleId] = useState<string | null>(null);
  const [photoPicker, setPhotoPicker] = useState<{
    puzzleId: string;
    answer: string;
    searchQuery: string;
    provider: PhotoProviderChoice;
  } | null>(null);
  const [previewPuzzle, setPreviewPuzzle] = useState<AdminPuzzle | null>(null);
  const [answer, setAnswer] = useState('');
  const [answerEn, setAnswerEn] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [aliases, setAliases] = useState('');
  const [publishDate, setPublishDate] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (pool) params.set('pool', pool);
    if (status) params.set('status', status);
    if (reviewed) params.set('reviewed', reviewed);
    if (sort) params.set('sort', sort);
    const qs = params.toString();
    api<{ puzzles: AdminPuzzle[] }>(`/admin/puzzles${qs ? `?${qs}` : ''}`)
      .then((res) => setPuzzles(res.puzzles))
      .catch(() => setError(t('common.error')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [pool, status, reviewed, sort]);

  const runBatch = async (batchPool: 'daily' | 'premium') => {
    setBatchBusy(batchPool);
    setBatchMsg('');
    try {
      const res = await api<{
        imported: number;
        remaining?: number;
        failures?: { answer: string; error: string }[];
      }>('/admin/puzzles/import-batch', {
        method: 'POST',
        body: JSON.stringify({ pool: batchPool }),
      });
      let msg = t('admin.puzzles.batchDone').replace('{n}', String(res.imported));
      if (res.failures?.length) {
        msg += ` ${t('admin.puzzles.batchFailures').replace('{n}', String(res.failures.length))}`;
        const first = res.failures[0];
        if (first) msg += ` (${first.answer}: ${first.error})`;
      }
      setBatchMsg(msg);
      load();
    } catch (e: unknown) {
      const err = e as { data?: { error?: string; message?: string; bankSize?: number; inDatabase?: number } };
      setBatchMsg(
        err.data?.error === 'bank_exhausted'
          ? t('admin.puzzles.batchExhausted')
              .replace('{bankSize}', String(err.data.bankSize ?? '?'))
              .replace('{inDatabase}', String(err.data.inDatabase ?? '?'))
          : err.data?.message || t('common.error'),
      );
    } finally {
      setBatchBusy(null);
    }
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    fd.append('answer', answer);
    fd.append('aliases', aliases);
    if (searchQuery.trim()) fd.append('searchQuery', searchQuery.trim());
    if (answerEn.trim()) fd.append('answerEn', answerEn.trim());
    if (publishDate) fd.append('publishDate', publishDate);

    const token = getToken();
    const res = await fetch('/api/admin/puzzles', {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) {
      setCreateMsg(data.error || t('common.error'));
      return;
    }
    setCreateMsg(`${t('admin.puzzles.created')} ${data.slug}`);
    setShowCreate(false);
    load();
  };

  const handleSaved = (updated: AdminPuzzle) => {
    setPuzzles((prev) => {
      if (reviewed === 'false' && updated.adminReviewed) {
        return prev.filter((p) => p.id !== updated.id);
      }
      if (reviewed === 'true' && !updated.adminReviewed) {
        return prev.filter((p) => p.id !== updated.id);
      }
      return prev.map((p) => (p.id === updated.id ? updated : p));
    });
  };

  return (
    <div className="admin-puzzles admin-puzzles-layout">
      <div className="admin-toolbar">
        <div className="admin-toolbar-batches">
          <div className="admin-batch-group">
            <span className="admin-batch-label">{t('admin.puzzles.poolDaily')}</span>
            <button
              type="button"
              className="btn secondary admin-btn-sm"
              disabled={batchBusy !== null}
              onClick={() => runBatch('daily')}
            >
              {batchBusy === 'daily' ? t('common.loading') : t('admin.puzzles.batchDaily')}
            </button>
          </div>
          <div className="admin-batch-group admin-batch-group--locked" title={t('admin.puzzles.poolPremiumLocked')}>
            <span className="admin-batch-label">{t('admin.puzzles.poolPremium')}</span>
            <button
              type="button"
              className="btn secondary admin-btn-sm"
              disabled
            >
              {t('admin.puzzles.locked')}
            </button>
          </div>
        </div>
        <div className="admin-toolbar-filters">
          <select value={pool} onChange={(e) => setPool(e.target.value)} aria-label={t('admin.puzzles.poolAll')}>
            <option value="daily">{t('admin.puzzles.poolDaily')}</option>
            <option value="">{t('admin.puzzles.poolAll')}</option>
            <option value="premium" disabled>{t('admin.puzzles.poolPremiumLocked')}</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t('admin.puzzles.statusAll')}>
            <option value="">{t('admin.puzzles.statusAll')}</option>
            <option value="draft">draft</option>
            <option value="scheduled">scheduled</option>
            <option value="published">published</option>
            <option value="active">active</option>
          </select>
          <select value={reviewed} onChange={(e) => setReviewed(e.target.value)} aria-label={t('admin.puzzles.reviewedAll')}>
            <option value="">{t('admin.puzzles.reviewedAll')}</option>
            <option value="false">{t('admin.puzzles.reviewedPending')}</option>
            <option value="true">{t('admin.puzzles.reviewedDone')}</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label={t('admin.puzzles.sort')}>
            <option value="number_asc">{t('admin.puzzles.sortNumberAsc')}</option>
            <option value="number_desc">{t('admin.puzzles.sortNumberDesc')}</option>
            <option value="date_asc">{t('admin.puzzles.sortDateAsc')}</option>
            <option value="date_desc">{t('admin.puzzles.sortDateDesc')}</option>
            <option value="review_first">{t('admin.puzzles.sortReviewFirst')}</option>
          </select>
          <button type="button" className="btn secondary admin-btn-sm" onClick={() => setShowCreate((v) => !v)}>
            {t('admin.puzzles.create')}
          </button>
        </div>
      </div>
      {batchMsg && <p className="admin-msg admin-msg--compact">{batchMsg}</p>}

      {showCreate && (
        <form className="admin-form admin-create-form" onSubmit={handleCreate}>
          <label className="admin-create-field--full">
            {t('admin.puzzles.image')}
            <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} required />
          </label>
          <div className="admin-form-row admin-form-row--2">
            <label>
              {t('admin.puzzles.answer')}
              <input value={answer} onChange={(e) => setAnswer(e.target.value)} required />
            </label>
            <label>
              {t('admin.puzzles.answerEn')}
              <input value={answerEn} onChange={(e) => setAnswerEn(e.target.value)} />
            </label>
          </div>
          <label>
            {t('admin.puzzles.searchQuery')}
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('admin.puzzles.searchQueryPlaceholder')}
            />
          </label>
          <label>
            {t('admin.puzzles.aliases')}
            <input value={aliases} onChange={(e) => setAliases(e.target.value)} />
          </label>
          <label>
            {t('admin.puzzles.publishDate')}
            <input type="date" value={publishDate} onChange={(e) => setPublishDate(e.target.value)} />
          </label>
          <button type="submit" className="btn primary admin-create-field--full">{t('admin.puzzles.create')}</button>
        </form>
      )}
      {createMsg && <p className="admin-msg">{createMsg}</p>}

      {loading && <p className="admin-msg">{t('common.loading')}</p>}
      {error && <p className="admin-msg">{error}</p>}

      {!loading && !error && puzzles.length === 0 && (
        <p className="admin-msg">{t('admin.puzzles.empty')}</p>
      )}

      <div className="admin-puzzle-grid admin-puzzle-grid--dense">
        {puzzles.map((puzzle) => (
          <PuzzleCard
            key={`${puzzle.id}-${puzzle.imageVersion ?? 0}`}
            puzzle={puzzle}
            onSaved={handleSaved}
            onEditCrops={() => setCropPuzzleId(puzzle.id)}
            onPreview={setPreviewPuzzle}
            onPickPhoto={(p, params) => setPhotoPicker({
              puzzleId: p.id,
              answer: params.answer,
              searchQuery: params.searchQuery,
              provider: params.provider,
            })}
          />
        ))}
      </div>

      {previewPuzzle && (
        <AdminPuzzlePreview
          puzzle={previewPuzzle}
          onClose={() => setPreviewPuzzle(null)}
        />
      )}

      {photoPicker && (
        <AdminPhotoPicker
          puzzleId={photoPicker.puzzleId}
          answer={photoPicker.answer}
          searchQuery={photoPicker.searchQuery}
          provider={photoPicker.provider}
          onClose={() => setPhotoPicker(null)}
          onApplied={(updated) => {
            handleSaved(updated as AdminPuzzle);
            setPhotoPicker(null);
          }}
        />
      )}

      {cropPuzzleId && (
        <AdminCropEditor
          puzzleId={cropPuzzleId}
          onClose={() => setCropPuzzleId(null)}
          onApplied={(updated) => {
            handleSaved(updated as AdminPuzzle);
            setCropPuzzleId(null);
          }}
        />
      )}
    </div>
  );
}
